import { Button } from '@prio365/prio365-react-library';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import Flex from '../../../../../../components/Flex';
import { PrioTheme } from '../../../../../../theme/types';
import { makePrioStyles } from '../../../../../../theme/utils';

type Mode = 'edit' | 'view' | 'review';

const useStyles = makePrioStyles((theme) => ({
  validationWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 24,
  },
  validation: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.base.red[80],
  },
}));

interface IncomingInvoiceDrawerActionsProps {
  mode: Mode;
  currentStep: number;
  isLoading: boolean;
  isFormValid: boolean;
  hasUnsavedChanges: boolean;
  amountFullyAllocated: boolean;
  allocationsValid: boolean;
  hasAllocations: boolean;
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
  onApprove: () => void;
  onReject: () => void;
  onCancel: () => void;
  onSaveWithAllocations?: (navigate?: boolean) => Promise<void>;
  skipAllocationStep: boolean;
  allocationStepOptional: boolean;
}

export const IncomingInvoiceDrawerActions: React.FC<
  IncomingInvoiceDrawerActionsProps
> = ({
  mode,
  currentStep,
  isLoading,
  isFormValid,
  hasUnsavedChanges,
  amountFullyAllocated,
  allocationsValid,
  hasAllocations,
  onPrev,
  onNext,
  onSubmit,
  onApprove,
  onReject,
  onCancel,
  onSaveWithAllocations,
  skipAllocationStep,
  allocationStepOptional,
}) => {
  //#region ------------------------------ Defaults
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  const classes = useStyles();
  //#endregion

  const [isActionLoading, setIsActionLoading] = useState(false);

  //#region ------------------------------ Methods
  const handleSaveAndContinue = async (navigate: boolean) => {
    if (onSaveWithAllocations) {
      setIsActionLoading(true);
      try {
        await onSaveWithAllocations(navigate);
      } catch (error) {
        console.error('Save operation failed:', error);
      } finally {
        setIsActionLoading(false);
      }
    }
  };
  //#endregion

  return (
    <>
      <div className={classes.validationWrapper}>
        <span className={classes.validation}>
          {currentStep === 2 &&
          (mode !== 'review' || hasAllocations) &&
          !skipAllocationStep &&
          !amountFullyAllocated &&
          !allocationStepOptional
            ? t(
                'accounting:incomingInvoices.drawerSteps.allocations.validation.amountNotFullyAllocated'
              )
            : currentStep === 1 && !isFormValid
              ? t(
                  'accounting:incomingInvoices.drawerSteps.controlling.validation.formInvalid'
                )
              : undefined}
        </span>
      </div>
      <Flex.Row justifyContent="space-between" alignItems="center">
        <Button type="link" onClick={onCancel}>
          {t('accounting:incomingInvoices.drawer.cancel')}
        </Button>

        {hasUnsavedChanges && (
          <Button
            type="link"
            loading={isLoading || isActionLoading}
            disabled={
              !isFormValid ||
              (currentStep === 2 &&
                (mode !== 'review' || hasAllocations) &&
                !skipAllocationStep &&
                !allocationStepOptional &&
                (!amountFullyAllocated || !allocationsValid))
            }
            onClick={() => handleSaveAndContinue(false)}
          >
            {t('accounting:incomingInvoices.drawer.save')}
          </Button>
        )}

        <Flex.Row alignItems="center" childrenGap={8}>
          {currentStep > 0 && mode !== 'view' && (
            <Button type="link" onClick={onPrev}>
              {t('accounting:incomingInvoices.drawer.back')}
            </Button>
          )}

          {currentStep === 3 ? (
            <>
              {mode === 'edit' && (
                <Button
                  type="primary"
                  disabled={
                    !isFormValid ||
                    isLoading ||
                    // Only require full allocation if step wasn't skipped and isn't optional
                    (!skipAllocationStep &&
                      !allocationStepOptional &&
                      !amountFullyAllocated)
                  }
                  loading={isLoading || isActionLoading}
                  onClick={onSubmit}
                >
                  {t('accounting:incomingInvoices.drawer.submit')}
                </Button>
              )}
              {mode === 'review' && (
                <>
                  <Button
                    type="link"
                    onClick={onReject}
                    disabled={!isFormValid || isLoading}
                    loading={isLoading || isActionLoading}
                    style={{
                      color: theme.colors.base.red.default,
                    }}
                  >
                    {t('accounting:incomingInvoices.drawer.reject')}
                  </Button>
                  <Button
                    type="primary"
                    iconProp={['fal', 'check']}
                    onClick={onApprove}
                    disabled={!isFormValid || isLoading}
                    loading={isLoading || isActionLoading}
                  >
                    {t('accounting:incomingInvoices.drawer.approve')}
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              {mode !== 'view' && (
                <Button
                  type="primary"
                  loading={isActionLoading}
                  disabled={
                    (currentStep === 2 &&
                      (mode !== 'review' || hasAllocations) &&
                      !skipAllocationStep &&
                      !allocationStepOptional &&
                      (!amountFullyAllocated ||
                        !isFormValid ||
                        !allocationsValid)) ||
                    (currentStep === 1 && !isFormValid)
                  }
                  onClick={() => {
                    if (currentStep === 2 && hasUnsavedChanges) {
                      handleSaveAndContinue(true);
                    } else {
                      onNext();
                    }
                  }}
                >
                  {currentStep === 2 && hasUnsavedChanges
                    ? t('accounting:incomingInvoices.drawer.saveAndContinue')
                    : t('accounting:incomingInvoices.drawer.next')}
                </Button>
              )}
            </>
          )}
        </Flex.Row>
      </Flex.Row>
    </>
  );
};
